import React, { Component } from "react"
import { connect } from "react-redux"
import { compose } from "redux"
import PropTypes from "prop-types"
import { translate } from "react-i18next"
import PulseCheckQuestionShape from "../../state/shapes/PulseCheckQuestionShape"
import Widget from "../layout/widget/Widget"
import GaugeComparisonChart from "../charts/generic/GaugeComparisonChart"
import ColumnLineChart from "../charts/generic/ColumnLineChart"
import SelectList from "../inputs/SelectList"
import { formatDateForDisplay } from "../../utils/formatting-utils"
import styled from "styled-components"

const DropdownArea = styled.div`
  margin-left: 2em;
  margin-right: 2em;
`

class PulseChecks extends Component {
  constructor(props) {
    super(props)

    this.state = {
      barChartData: this.getBarChartData(this.props.data.summaries[0]),
      gaugeData: this.getGaugeData(this.props.data.summaries[0]),
      extraExportParameter: `&pulseCheckQuestionId=${this.props.data.summaries[0].questionId}`
    }
  }

  onSelectChange = e => {
    const questionId = Number(e.target.value)
    const questionSummary = this.getQuestionData(questionId)

    this.setState(() => {
      const extraExportParameter = `&pulseCheckQuestionId=${questionId}`
      const barChartData = this.getBarChartData(questionSummary)
      const gaugeData = this.getGaugeData(questionSummary)

      return {
        barChartData,
        gaugeData,
        extraExportParameter
      }
    })
  }

  getQuestionsFromApi = () => {
    return this.props.data.questions
  }

  getQuestionData = selectedQuestionId => {
    return this.props.data.summaries.find(summary => summary.questionId === selectedQuestionId)
  }

  getGaugeData = questionSummary => {
    const gaugeData = []
    // use a copy of the array to avoid state mutation message.
    const copyArray = questionSummary.answerSummaries.map(answer => ({ ...answer }))
    copyArray.reverse()
    copyArray.forEach(a => {
      const formattedDate = formatDateForDisplay(a.questionAskedDate)

      // Create gauge key value pairs
      const gaugeKeyExists = gaugeData.find(gaugeItem => {
        return gaugeItem.itemName === formattedDate
      })

      const percentage = Math.round(a.answerCount / (a.employeeTotal / 100))

      if (!gaugeKeyExists) gaugeData.push({ itemName: formattedDate, value: percentage })
      else gaugeKeyExists.value += percentage
    })

    return gaugeData
  }

  getBarChartData = questionSummary => {
    const barChartData = {
      categories: [],
      series: []
    }

    questionSummary.answerSummaries.forEach(a => {
      const formattedDate = formatDateForDisplay(a.questionAskedDate)

      // Add categories
      if (!barChartData.categories.includes(a.answerValue)) {
        barChartData.categories.push(a.answerValue)
      }

      // Add column series
      const seriesExists = barChartData.series.find(s => {
        return s.name === formattedDate
      })
      if (!seriesExists) {
        const seriesColumnObj = {
          type: "column",
          name: formattedDate,
          data: [a.answerCount]
        }
        barChartData.series.push(seriesColumnObj)
      } else {
        seriesExists.data.push(a.answerCount)
      }
    })

    // Sort column series
    barChartData.series.sort((a, b) => {
      let dateA = new Date(a.name),
        dateB = new Date(b.name)
      return dateA - dateB
    })

    const averageData = []

    questionSummary.answerSummaries.forEach(a => {
      const exists = averageData.find(s => {
        return s.key === a.answerValue
      })

      if (!exists) {
        averageData.push({ key: a.answerValue, total: a.answerCount, count: 1 })
      } else {
        exists.total += a.answerCount
        exists.count += 1
      }
    })

    const averageTranslation = this.props.t("Common.Average")
    averageData.forEach(d => {
      const average = d.total / d.count

      // Add line series
      const averageExists = barChartData.series.find(s => {
        return s.name === averageTranslation
      })
      if (!averageExists) {
        const seriesLineObj = {
          type: "spline",
          name: averageTranslation,
          data: [average],
          marker: {
            lineWidth: 2
          }
        }
        barChartData.series.push(seriesLineObj)
      } else {
        averageExists.data.push(average)
      }
    })
    return barChartData
  }

  render() {
    const { t, exportEndpoint, appliedFilters } = this.props
    const titleKey="Widgets.PulseChecks.Title"
    return (
      <Widget
        columns={3}
        titleKey={titleKey}
        helpKey="Widgets.PulseChecks.Help"
        exportEndpoint={exportEndpoint}
        appliedFilters={appliedFilters}
        exportParams={this.state.extraExportParameter}
      >
        <div className="col-lg-12 col-md-12">
          <DropdownArea className="form-group">
            <SelectList onSelectChange={this.onSelectChange} data={this.getQuestionsFromApi()} />
          </DropdownArea>
          <div className="row">
            <div className="col-lg-8 col-md-12">
              <ColumnLineChart
                data={this.state.barChartData}
                yAxisTitle={t("Common.Responses")}
                yLineAxisTitle={t("Common.Average")}
                xAxisTitle={t("Common.Value")}
                titleKey={titleKey}
              />
            </div>
            <div className="col-lg-4 col-md-12">
              <GaugeComparisonChart
                data={this.state.gaugeData}
                percentageBasedChart
                labelText="%"
                reverseLegend
                titleKey={titleKey}
              />
            </div>
          </div>
        </div>
      </Widget>
    )
  }
}

PulseChecks.propTypes = {
  appliedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.shape(PulseCheckQuestionShape).isRequired,
  exportEndpoint: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired
}

function mapStateToProps(state) {
  return {
    filterState: state.filters
  }
}

export default compose(translate(), connect(mapStateToProps))(PulseChecks)



// WEBPACK FOOTER //
// ./src/components/widgets/PulseChecks.jsx